import React from "react";

import FilterToggleChip from "@/main/react/component/FilterToggleChip";
import type { Localizer } from "@/main/react/Types";

/**
 * The "Questions" filter pill — the one control that narrows a list of posts to the ones asking
 * something (the QUESTION facet, `facets & 2`).
 *
 * <p><b>One component, two consumers</b>: the group page's discussions section and the search page's
 * discussions tab. They are unrelated screens, so the tempting shape is a bespoke pill in each — which
 * is how one capability ends up wearing two different affordances that then drift apart. Both surfaces
 * hit the same backend parameter (`questionsOnly`), so they get the same control.
 *
 * <p><b>It owns no state and writes no history.</b> Each host keeps the flag in its own URL-backed
 * route state (`useHistoryBackedState`) and hands it down. That is deliberate: rule 33 allows exactly
 * one history writer, and a control that knew about the URL would be a second place for a `pushState`
 * to appear. It is also what makes this testable on its own.
 *
 * <p><b>No new copy key.</b> The label is `discussion.question.filter.chip` ("Questions"), minted in
 * M6 for precisely this and covered by the `discussion.question.` pending-translation exemption. Adding
 * a key here would put fresh English text directly beside the *translated* ordering select and language
 * picker, which is rule 11's adjacency trap — the reader does not evaluate a string, they read a row
 * that changes language halfway across.
 *
 * <p>Flat-X design language (rule 23): a pill, elevation by border rather than shadow, one hover wash,
 * one focus ring, semantic tokens only.
 *
 * <p><b>Its height deliberately matches `SimpleSelect size="sm"` exactly</b> — `min-h-9`, rising to
 * `min-h-11` under a coarse pointer for the 44px tap target. That is not cosmetic symmetry with the
 * ordering select and the language picker it sits beside: on the group page the tab-bar-to-content gap
 * is measured to the first PAINTED pixel below the tab bar and asserted equal across every tab
 * (`tests/group-page-layout-invariants.spec.mjs`). A chip even 2px shorter than its sibling control
 * would centre 1px lower and shift that measurement on the discussions tab alone. Matching the
 * sibling's floor in both pointer modes leaves the topmost painted edge exactly where it was.
 */
export default function QuestionsFilterChip({
    active,
    onToggle,
    localizer,
    disabled = false,
    dataPlaywrightId = "questions-filter-chip",
}: {
    active: boolean;
    /** Called with the NEXT value, never with a toggle signal — the host decides what to do with it. */
    onToggle: (next: boolean) => void;
    localizer: Localizer;
    disabled?: boolean;
    dataPlaywrightId?: string;
}) {
    // The pill itself (markup, states, height contract) lives in `FilterToggleChip` since SV-142 gave
    // it a second label; this wrapper keeps the Questions label and its two consumers unchanged.
    return (
        <FilterToggleChip
            active={active}
            onToggle={onToggle}
            label={localizer("discussion.question.filter.chip")}
            disabled={disabled}
            dataPlaywrightId={dataPlaywrightId}
        />
    );
}
